/**
 * EstimatesModule Component
 * Lists and manages estimates in the Sales module
 * Extracted from App.js for better maintainability
 */
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return amount.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// HtmlPreviewModal component
const HtmlPreviewModal = ({ title, url, onClose, testId }) => {
  if (!url) return null;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-2 sm:p-4" data-testid={testId || 'html-preview-modal'}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[92vh] sm:h-[86vh] flex flex-col">
        <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-200">
          <h3 className="text-sm sm:text-lg font-semibold text-gray-800 truncate">{title}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 p-1">✕</button>
        </div>
        <div className="flex-1 bg-gray-100 overflow-auto">
          <iframe src={url} title={title} className="w-full h-full border-0" style={{ minHeight: '100%' }} key={url} />
        </div>
      </div>
    </div>
  );
};

const EstimatesModule = ({ onBack, onEdit, onCreate, onCreateInvoice, onOpenNotifications }) => {
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [previewEstimate, setPreviewEstimate] = useState(null);
  const [openMobileActionsId, setOpenMobileActionsId] = useState(null);
  const [loadError, setLoadError] = useState('');
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    fetchEstimates();
    const interval = setInterval(() => fetchEstimates(true), 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchEstimates = async (silent = false) => {
    if (!silent) {
      setLoading(true);
    }
    try {
      const response = await axios.get(`${API}/sales/estimates`);
      setEstimates(response.data);
      setLoadError('');
      hasLoadedRef.current = true;
    } catch (error) {
      if (error.response?.status === 401) {
        return;
      }
      console.error('Error fetching estimates:', error);
      setLoadError('Unable to refresh estimates. Please reload.');
      if (!hasLoadedRef.current) {
        setEstimates([]);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const handleCreateInvoice = async (estimate) => {
    try {
      const response = await axios.post(`${API}/sales/estimates/${estimate.id}/invoice`);
      if (onCreateInvoice) {
        onCreateInvoice(response.data);
      }
    } catch (error) {
      console.error('Error creating invoice from estimate:', error);
      alert('Unable to create invoice from this estimate.');
    }
  };

  const handleDuplicateEstimate = async (estimate) => {
    try {
      const response = await axios.post(`${API}/sales/estimates/${estimate.id}/duplicate`);
      onEdit(response.data);
    } catch (error) {
      console.error('Error duplicating estimate:', error);
      alert('Unable to duplicate this estimate.');
    }
  };

  const handlePreviewEstimate = async (estimate) => {
    try {
      let token = estimate.public_token;
      let estimateNumber = estimate.estimate_number || '';
      if (!token) {
        const response = await axios.get(`${API}/sales/estimates/${estimate.id}`);
        token = response.data?.public_token;
        estimateNumber = response.data?.estimate_number || estimateNumber;
      }
      if (!token) {
        throw new Error('Preview link unavailable');
      }
      setPreviewEstimate({ token, estimate_number: estimateNumber });
    } catch (error) {
      console.error('Error opening preview:', error);
      alert('Unable to open preview.');
    }
  };

  const filteredEstimates = estimates.filter(estimate => {
    const matchesSearch = 
      estimate.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      estimate.client_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      estimate.estimate_number?.toString().includes(searchTerm) ||
      estimate.po_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesStatus = false;
    if (statusFilter === 'PENDING') {
      matchesStatus = estimate.status === 'DRAFT' || estimate.status === 'ISSUED';
    } else if (statusFilter === 'APPROVED') {
      matchesStatus = estimate.status === 'APPROVED';
    } else if (statusFilter === 'DECLINED') {
      matchesStatus = estimate.status === 'DECLINED';
    }
    
    return matchesSearch && matchesStatus;
  });

  // Group by month
  const groupedEstimates = filteredEstimates.reduce((groups, estimate) => {
    const date = new Date(estimate.date);
    const monthYear = date.toLocaleString('en-CA', { month: 'long', year: 'numeric' });
    if (!groups[monthYear]) {
      groups[monthYear] = [];
    }
    groups[monthYear].push(estimate);
    return groups;
  }, {});

  const getStatusBadge = (status) => {
    const styles = {
      'DRAFT': 'bg-gray-200 text-gray-800',
      'ISSUED': 'bg-blue-200 text-blue-800',
      'APPROVED': 'bg-green-200 text-green-800',
      'DECLINED': 'bg-red-200 text-red-800'
    };
    return styles[status] || styles['DRAFT'];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="sm:hidden flex items-center justify-between mb-3">
            <div className="w-6" />
            <h1 className="text-base font-semibold text-gray-900" data-testid="sales-estimates-mobile-title">Estimates</h1>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onOpenNotifications && onOpenNotifications()}
                className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center text-gray-700"
                data-testid="sales-estimates-mobile-notifications"
              >
                🔔
              </button>
              <button
                type="button"
                onClick={onCreate}
                className="w-9 h-9 rounded-full bg-green-600 text-white flex items-center justify-center text-lg"
                data-testid="sales-estimates-mobile-add"
              >
                +
              </button>
            </div>
          </div>

          <div className="hidden sm:flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 mb-4">
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                data-testid="sales-estimates-export"
              >
                Export
              </button>
              <button
                onClick={onCreate}
                className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
                data-testid="sales-estimates-new"
              >
                New Estimate
              </button>
            </div>
          </div>

          {/* Search */}
          <input
            type="text"
            placeholder="Search all estimates"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2.5 rounded-full sm:rounded-lg bg-gray-200 sm:bg-white border border-transparent sm:border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-4"
            data-testid="sales-estimates-search"
          />
          {loadError && (
            <div className="text-xs text-red-600 mb-3" data-testid="sales-estimates-load-error">
              {loadError}
            </div>
          )}

          {/* Status Tabs (Mobile) */}
          <div className="sm:hidden bg-gray-200 rounded-full p-1 flex gap-1" data-testid="sales-estimates-mobile-tabs">
            {['PENDING', 'APPROVED', 'DECLINED'].map(status => {
              const count = estimates.filter(e => {
                if (status === 'PENDING') return e.status === 'DRAFT' || e.status === 'ISSUED';
                return e.status === status;
              }).length;
              return (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`flex-1 text-xs font-semibold px-2 py-2 rounded-full transition ${
                    statusFilter === status
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600'
                  }`}
                  data-testid={`sales-estimates-mobile-tab-${status.toLowerCase()}`}
                >
                  {status.charAt(0) + status.slice(1).toLowerCase()}
                  {count > 0 && <span className="ml-1">{count}</span>}
                </button>
              );
            })}
          </div>

          {/* Status Tabs (Desktop) */}
          <div className="hidden sm:flex flex-wrap gap-3 sm:gap-6 border-b">
            {['PENDING', 'APPROVED', 'DECLINED'].map(status => {
              const count = estimates.filter(e => {
                if (status === 'PENDING') return e.status === 'DRAFT' || e.status === 'ISSUED';
                return e.status === status;
              }).length;
              return (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`pb-3 px-2 text-sm sm:text-base font-semibold transition-colors relative ${
                    statusFilter === status
                      ? 'text-green-600 border-b-2 border-green-600'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                  data-testid={`sales-estimates-tab-${status.toLowerCase()}`}
                >
                  {status.charAt(0) + status.slice(1).toLowerCase()}
                  {count > 0 && <span className="ml-1 text-sm">({count})</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="text-xl text-gray-600">Loading estimates...</div>
          </div>
        ) : Object.keys(groupedEstimates).length === 0 ? (
          <div className="text-center py-12">
            <div className="text-xl text-gray-600" data-testid="sales-estimates-empty">No estimates found</div>
            <button
              onClick={onCreate}
              className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold"
              data-testid="sales-estimates-create-first"
            >
              Create Your First Estimate
            </button>
          </div>
        ) : (
          Object.entries(groupedEstimates).map(([monthYear, monthEstimates]) => {
            const monthTotal = monthEstimates.reduce((sum, est) => sum + est.total, 0);
            return (
              <div key={monthYear} className="mb-8">
                <div className="hidden sm:flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800" data-testid={`sales-estimates-month-${monthYear.replace(/\s/g, '-')}`}>
                    {monthYear}
                  </h2>
                  <div className="text-base sm:text-lg font-semibold text-gray-600" data-testid={`sales-estimates-month-total-${monthYear.replace(/\s/g, '-')}`}>
                    Total: ${formatCurrency(monthTotal)}
                  </div>
                </div>
                <div className="sm:hidden text-base font-semibold text-gray-800 mb-2 px-1" data-testid={`sales-estimates-month-mobile-${monthYear.replace(/\s/g, '-')}`}>
                  {monthYear}
                </div>
                <div className="hidden sm:block space-y-3">
                  {monthEstimates.map(estimate => (
                    <div
                      key={estimate.id}
                      className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-shadow cursor-pointer"
                      data-testid={`sales-estimate-card-${estimate.id}`}
                      onClick={() => onEdit(estimate)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onEdit(estimate);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <h3 className="text-lg font-bold text-gray-900" data-testid={`sales-estimate-client-${estimate.id}`}>
                              {estimate.client_name} - #{estimate.estimate_number}
                            </h3>
                            <span className={`px-2.5 py-0.5 rounded text-xs font-semibold ${getStatusBadge(estimate.status)}`} data-testid={`sales-estimate-status-${estimate.id}`}>
                              {estimate.status}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 mb-3" data-testid={`sales-estimate-date-${estimate.id}`}>
                            {new Date(estimate.date).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}
                          </div>
                          <div className="text-sm text-gray-700 space-y-0.5" data-testid={`sales-estimate-contact-${estimate.id}`}>
                            <div>{estimate.client_address}</div>
                            <div>{estimate.client_city}{estimate.client_province ? `, ${estimate.client_province}` : ''} {estimate.client_postal_code}</div>
                            <div>{estimate.client_phone}</div>
                          </div>
                          <div className="mt-3" data-testid={`sales-estimate-opened-${estimate.id}`}>
                            <div className={`flex items-center gap-1.5 text-sm ${estimate.email_opened_count > 0 ? 'text-emerald-700' : 'text-gray-500'}`} data-testid={`sales-estimate-opened-status-${estimate.id}`}>
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                              </svg>
                              {estimate.email_opened_count > 0 ? `Opened ${estimate.email_opened_count}x` : 'Not opened yet'}
                            </div>
                            <div className="text-xs text-gray-500 mt-1" data-testid={`sales-estimate-last-opened-${estimate.id}`}>
                              Last opened: {estimate.last_opened_at ? new Date(estimate.last_opened_at).toLocaleString('en-CA') : '—'}
                            </div>
                          </div>
                        </div>
                        <div className="w-full lg:w-auto text-left lg:text-right flex flex-col items-start lg:items-end">
                          <div className="text-xl sm:text-2xl font-bold text-gray-900 mb-3" data-testid={`sales-estimate-total-${estimate.id}`}>
                            ${formatCurrency(estimate.total)}
                          </div>
                          <div className="flex flex-wrap gap-2 w-full">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePreviewEstimate(estimate);
                              }}
                              className="w-full sm:w-auto bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-1.5 rounded-md font-medium text-sm transition-colors"
                              data-testid={`sales-estimate-preview-${estimate.id}`}
                            >
                              Preview
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCreateInvoice(estimate);
                              }}
                              className="w-full sm:w-auto bg-white border border-indigo-300 hover:bg-indigo-50 text-indigo-700 px-3 py-1.5 rounded-md text-sm transition-colors"
                              data-testid={`sales-estimate-create-invoice-${estimate.id}`}
                            >
                              Invoice
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDuplicateEstimate(estimate);
                              }}
                              className="w-full sm:w-auto bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-md text-sm transition-colors"
                              data-testid={`sales-estimate-duplicate-${estimate.id}`}
                            >
                              Duplicate
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="sm:hidden bg-white border border-gray-200 rounded-xl divide-y" data-testid={`sales-estimates-mobile-group-${monthYear.replace(/\s/g, '-')}`}>
                  {monthEstimates.map(estimate => (
                    <div
                      key={estimate.id}
                      className="px-4 py-3 flex items-start justify-between gap-3"
                      data-testid={`sales-estimate-row-${estimate.id}`}
                      onClick={() => {
                        setOpenMobileActionsId(null);
                        onEdit(estimate);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setOpenMobileActionsId(null);
                          onEdit(estimate);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 truncate" data-testid={`sales-estimate-row-client-${estimate.id}`}>
                          {estimate.client_name} - #{estimate.estimate_number}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5" data-testid={`sales-estimate-row-date-${estimate.id}`}>
                          {new Date(estimate.date).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
                        </div>
                        <div className="flex items-center gap-2 mt-1" data-testid={`sales-estimate-row-status-${estimate.id}`}>
                          <span className={`text-xs ${estimate.email_opened_count > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
                            {estimate.email_opened_count > 0 ? `Opened ${estimate.email_opened_count}x` : 'Not opened'}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${getStatusBadge(estimate.status)}`}>
                            {estimate.status}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <div className="text-sm font-semibold text-gray-900" data-testid={`sales-estimate-row-total-${estimate.id}`}>
                          ${formatCurrency(estimate.total)}
                        </div>
                        <div className="relative">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMobileActionsId(prev => (prev === estimate.id ? null : estimate.id));
                            }}
                            className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500"
                            data-testid={`sales-estimate-row-actions-${estimate.id}`}
                          >
                            ⋯
                          </button>
                          {openMobileActionsId === estimate.id && (
                            <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-20" data-testid={`sales-estimate-row-actions-menu-${estimate.id}`}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenMobileActionsId(null);
                                  handlePreviewEstimate(estimate);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                data-testid={`sales-estimate-row-preview-${estimate.id}`}
                              >
                                Preview
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenMobileActionsId(null);
                                  handleCreateInvoice(estimate);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                data-testid={`sales-estimate-row-invoice-${estimate.id}`}
                              >
                                Create Invoice
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenMobileActionsId(null);
                                  handleDuplicateEstimate(estimate);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                data-testid={`sales-estimate-row-duplicate-${estimate.id}`}
                              >
                                Duplicate
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
      {previewEstimate && (
        <HtmlPreviewModal
          title={`Estimate Preview #${previewEstimate.estimate_number || ''}`}
          url={`${window.location.origin}/estimate/${previewEstimate.token}?mode=preview`}
          onClose={() => setPreviewEstimate(null)}
          testId="estimate-preview-modal"
        />
      )}
    </div>
  );
};

export default EstimatesModule;
